import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import vocabulariumBlason from "../assets/vocabularium_blason.png";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import { formatParisDateFR, formatParisTimeFR } from "../utils/parisWallClock";
import {
  deleteOfficialAnnouncement,
  fetchOfficialCatalog,
  publishOfficialAnnouncement,
  saveOfficialWelcome,
  updateOfficialAnnouncement,
} from "../utils/kingdomMessaging";
import NotesConfirmModal from "./NotesConfirmModal";
import "./WordEditPanel.css";
import "./ControlPanelView.css";
import "./OfficialMessagesAdminView.css";

const OFFICIAL_MAX_CHARS = 2000;
const NEW_ENTRY_ID = "__new";
const NEW_NEWS_ID = "__new_news";
const WELCOME_ENTRY_ID = "welcome";

function officialStamp(iso) {
  const date = formatParisDateFR(iso);
  const time = formatParisTimeFR(iso);
  if (!date) return "";
  return time ? `${date} · ${time}` : date;
}

function OfficialSeal() {
  return (
    <img
      className="cpanel-official__seal"
      src={vocabulariumBlason}
      alt="Blason de Vocabularium"
    />
  );
}

export default function OfficialMessagesAdminView() {
  const listRef = useRef(null);
  const editorRef = useRef(null);
  const {
    trackRef: listTrackRef,
    scrollbar: listScrollbar,
    updateScrollbar: updateListScrollbar,
    handleThumbPointerDown: handleListThumbPointerDown,
    handleTrackPointerDown: handleListTrackPointerDown,
  } = useCustomScrollbar(listRef);
  const {
    trackRef: editorTrackRef,
    scrollbar: editorScrollbar,
    updateScrollbar: updateEditorScrollbar,
    handleThumbPointerDown: handleEditorThumbPointerDown,
    handleTrackPointerDown: handleEditorTrackPointerDown,
  } = useCustomScrollbar(editorRef);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [welcome, setWelcome] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedId, setSelectedId] = useState(WELCOME_ENTRY_ID);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const entries = useMemo(() => {
    const official = [
      { id: NEW_ENTRY_ID, kind: "new", channel: "official", title: "Nouvelle annonce", subtitle: "" },
    ];
    if (welcome) {
      official.push({
        id: WELCOME_ENTRY_ID,
        kind: "welcome",
        channel: "official",
        title: "Bienvenue",
        subtitle: officialStamp(welcome.updatedAt || welcome.createdAt),
        body: welcome.body,
      });
    }
    const news = [
      { id: NEW_NEWS_ID, kind: "new", channel: "news", title: "Nouvelle nouveauté", subtitle: "" },
    ];
    for (const item of announcements) {
      const entry = {
        id: item.id,
        kind: "announcement",
        channel: item.channel === "news" ? "news" : "official",
        title: officialStamp(item.createdAt) || "Annonce",
        subtitle:
          item.updatedAt && item.updatedAt !== item.createdAt
            ? `modifié ${officialStamp(item.updatedAt)}`
            : "",
        body: item.body,
      };
      if (entry.channel === "news") news.push(entry);
      else official.push(entry);
    }
    return { official, news, all: [...official, ...news] };
  }, [welcome, announcements]);

  const selected = entries.all.find((item) => item.id === selectedId) || entries.all[0];

  const load = useCallback(async () => {
    const json = await fetchOfficialCatalog();
    const nextWelcome = json.welcome || null;
    const nextAnnouncements = Array.isArray(json.announcements) ? json.announcements : [];
    setWelcome(nextWelcome);
    setAnnouncements(nextAnnouncements);
    return { welcome: nextWelcome, announcements: nextAnnouncements };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const payload = await load();
        if (cancelled) return;
        setSelectedId(payload.welcome ? WELCOME_ENTRY_ID : NEW_ENTRY_ID);
        setDraft(payload.welcome?.body || "");
      } catch (err) {
        if (!cancelled) setError(err.message || "Chargement impossible.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    if (selected.kind === "new") {
      setDraft("");
      return;
    }
    setDraft(selected.body || "");
  }, [selected]);

  useEffect(() => {
    const nodes = [listRef.current, editorRef.current].filter(Boolean);
    if (!nodes.length) return undefined;
    const frame = requestAnimationFrame(() => {
      updateListScrollbar();
      updateEditorScrollbar();
    });
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            updateListScrollbar();
            updateEditorScrollbar();
          })
        : null;
    nodes.forEach((node) => observer?.observe(node));
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [updateListScrollbar, updateEditorScrollbar, loading, selectedId, draft, entries.all.length]);

  const savedBody =
    selected?.kind === "welcome"
      ? String(welcome?.body || "")
      : selected?.kind === "announcement"
        ? String(selected.body || "")
        : "";
  const dirty = draft.trim() !== savedBody.trim();
  const canSubmit = Boolean(draft.trim()) && (selected?.kind === "new" || dirty);

  const selectEntry = (entry) => {
    setError("");
    setInfo("");
    setSelectedId(entry.id);
  };

  const run = async (action, successMessage, nextId) => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const createdId = await action();
      const payload = await load();
      const target = createdId || nextId;
      const exists =
        target &&
        (target === NEW_ENTRY_ID ||
          target === NEW_NEWS_ID ||
          target === WELCOME_ENTRY_ID ||
          payload.announcements.some((item) => item.id === target));
      const fallback = payload.welcome ? WELCOME_ENTRY_ID : NEW_ENTRY_ID;
      setSelectedId(exists ? target : fallback);
      if (successMessage) setInfo(successMessage);
    } catch (err) {
      setError(err.message || "Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = () => {
    if (!selected || !canSubmit || busy) return;
    if (selected.kind === "welcome") {
      run(() => saveOfficialWelcome(draft), "Le message de bienvenue a été mis à jour.", WELCOME_ENTRY_ID);
      return;
    }
    if (selected.kind === "new") {
      const news = selected.channel === "news";
      run(async () => {
        const result = await publishOfficialAnnouncement(draft, news ? "news" : "official");
        return result.announcement?.id;
      }, news ? "La nouveauté a été publiée." : "L'annonce a été publiée.");
      return;
    }
    run(
      () => updateOfficialAnnouncement(selected.id, draft),
      "L'annonce a été modifiée.",
      selected.id
    );
  };

  const submitLabel =
    selected?.kind === "new"
      ? selected.channel === "news"
        ? "Publier la nouveauté"
        : "Publier l'annonce"
      : selected?.kind === "welcome"
        ? "Enregistrer le message de bienvenue"
        : "Enregistrer les modifications";

  return (
    <div className="cpanel-view cpanel-view--stats cpanel-official">
      <header className="cpanel-view__header">
        <h2 className="cpanel-view__title">Messages du Royaume</h2>
        <p className="cpanel-view__intro">
          Messages officiels pour les annonces du Royaume, Nouveautés pour le
          développement. Les joueurs ne peuvent répondre à aucune des deux.
        </p>
      </header>

      {error ? <p className="cpanel-view__banner cpanel-view__banner--error">{error}</p> : null}
      {info ? <p className="cpanel-view__banner cpanel-view__banner--info">{info}</p> : null}

      {loading ? (
        <p className="cpanel-view__empty">Chargement…</p>
      ) : (
        <div className="cpanel-official__split">
          <aside className="cpanel-official__nav">
            <div
              className={[
                "cpanel-scroll-shell",
                listScrollbar.visible ? "cpanel-scroll-shell--scrollable" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="cpanel-scroll-fade-host">
                <div
                  ref={listRef}
                  className="cpanel-scroll-body cpanel-official__nav-body"
                  onScroll={updateListScrollbar}
                >
                  <p className="cpanel-official__nav-label">Messages officiels</p>
                  {entries.official.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className={`cpanel-official__nav-item${
                        entry.id === selected?.id ? " cpanel-official__nav-item--active" : ""
                      }`}
                      onClick={() => selectEntry(entry)}
                    >
                      <span className="cpanel-official__nav-title">{entry.title}</span>
                      {entry.subtitle ? (
                        <span className="cpanel-official__nav-sub">{entry.subtitle}</span>
                      ) : null}
                    </button>
                  ))}
                  <p className="cpanel-official__nav-label">Nouveautés</p>
                  {entries.news.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className={`cpanel-official__nav-item${
                        entry.id === selected?.id ? " cpanel-official__nav-item--active" : ""
                      }`}
                      onClick={() => selectEntry(entry)}
                    >
                      <span className="cpanel-official__nav-title">{entry.title}</span>
                      {entry.subtitle ? (
                        <span className="cpanel-official__nav-sub">{entry.subtitle}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
              <div
                ref={listTrackRef}
                className={[
                  "cpanel-scroll-bar",
                  !listScrollbar.visible ? "cpanel-scroll-bar--hidden" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-hidden="true"
                onPointerDown={handleListTrackPointerDown}
              >
                {listScrollbar.visible ? (
                  <div
                    className="cpanel-scroll-bar__thumb"
                    style={{
                      height: `${listScrollbar.height}px`,
                      transform: `translateY(${listScrollbar.top}px)`,
                    }}
                    onPointerDown={handleListThumbPointerDown}
                  />
                ) : null}
              </div>
            </div>
          </aside>

          <section className="cpanel-official__editor">
            <div
              className={[
                "cpanel-scroll-shell",
                editorScrollbar.visible ? "cpanel-scroll-shell--scrollable" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="cpanel-scroll-fade-host">
                <form
                  ref={editorRef}
                  className="cpanel-scroll-body cpanel-official__form"
                  onScroll={updateEditorScrollbar}
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="cpanel-official__editor-head">
                    <h3 className="cpanel-official__title">
                      {selected?.kind === "new"
                        ? selected.channel === "news"
                          ? "Nouvelle nouveauté"
                          : "Nouvelle annonce"
                        : selected?.kind === "welcome"
                          ? "Message de bienvenue"
                          : selected?.title || "Annonce"}
                    </h3>
                    {selected?.kind === "announcement" ? (
                      <button
                        type="button"
                        className="word-edit-btn word-edit-btn--danger"
                        onClick={() => setPendingDelete(selected)}
                        disabled={busy}
                      >
                        Supprimer
                      </button>
                    ) : null}
                  </div>
                  <p className="cpanel-official__hint">
                    {selected?.kind === "new" && selected.channel === "news"
                      ? "Évolution, nouvelle fonctionnalité, correction… Le message part dans Nouveautés. Les joueurs ne peuvent pas y répondre."
                      : selected?.kind === "new"
                        ? "Annonce du Royaume, envoyée dans Messages officiels. Les joueurs ne peuvent pas y répondre."
                        : selected?.kind === "welcome"
                          ? "Premier message officiel vu à l'arrivée dans le Royaume."
                          : selected?.channel === "news"
                            ? "Les joueurs voient ce texte dans Nouveautés."
                            : "Les joueurs voient la version enregistrée dans Messages officiels."}
                  </p>
                  <textarea
                    id="official-editor"
                    className="cpanel-official__textarea"
                    value={draft}
                    maxLength={OFFICIAL_MAX_CHARS}
                    rows={12}
                    onChange={(event) => setDraft(event.target.value)}
                    disabled={busy}
                    aria-label="Texte du message officiel"
                  />
                  <div className="cpanel-official__form-bar">
                    <span className="cpanel-official__count">
                      {draft.length} / {OFFICIAL_MAX_CHARS}
                    </span>
                    <button
                      type="submit"
                      className="word-edit-btn word-edit-btn--primary"
                      disabled={busy || !canSubmit}
                    >
                      {busy ? "Enregistrement…" : submitLabel}
                    </button>
                  </div>
                  <div className="cpanel-official__preview" aria-label="Aperçu du message">
                    <p className="cpanel-official__preview-text">
                      {draft.trim() || "L'aperçu apparaîtra ici."}
                    </p>
                    <OfficialSeal />
                  </div>
                </form>
              </div>
              <div
                ref={editorTrackRef}
                className={[
                  "cpanel-scroll-bar",
                  !editorScrollbar.visible ? "cpanel-scroll-bar--hidden" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-hidden="true"
                onPointerDown={handleEditorTrackPointerDown}
              >
                {editorScrollbar.visible ? (
                  <div
                    className="cpanel-scroll-bar__thumb"
                    style={{
                      height: `${editorScrollbar.height}px`,
                      transform: `translateY(${editorScrollbar.top}px)`,
                    }}
                    onPointerDown={handleEditorThumbPointerDown}
                  />
                ) : null}
              </div>
            </div>
          </section>
        </div>
      )}

      <NotesConfirmModal
        open={Boolean(pendingDelete)}
        title="Supprimer cette annonce ?"
        message="Elle disparaîtra de la messagerie de tous les joueurs."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const item = pendingDelete;
          setPendingDelete(null);
          if (!item) return;
          run(
            () => deleteOfficialAnnouncement(item.id),
            "L'annonce a été retirée.",
            WELCOME_ENTRY_ID
          );
        }}
      />
    </div>
  );
}
